import { useState } from "react";
import { toast } from "sonner";
import { MessageCircleQuestion, Clock, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const hasStorage = (b) => (b.inclusions?.items || []).includes("storage");

// Customer side: one-tap question on a bid without storage, then its status.
export function StorageQuestion({ job, bid, onAsked, compact = false }) {
  const [busy, setBusy] = useState(false);
  if (!(job.storage_days > 0) || job.status !== "open" || bid.status !== "pending") return null;
  const q = bid.storage_question;
  if (hasStorage(bid)) {
    return q?.answered_at ? <p className="flex items-center gap-1 text-xs text-emerald-700" data-testid={`storage-q-answered-${bid.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Storage added after your question</p> : null;
  }
  if (q?.asked_at) {
    return <p className="flex items-center gap-1 text-xs text-amber-700" data-testid={`storage-q-pending-${bid.id}`}><Clock className="h-3.5 w-3.5" /> Storage question sent, waiting for an updated bid</p>;
  }
  if (bid.is_expired) return null;
  const ask = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bids/${bid.id}/storage-question`);
      toast.success(`Asked ${bid.removalist_name} how they'd store your goods`);
      onAsked && onAsked(bid.id, data.storage_question);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" disabled={busy} onClick={ask} data-testid={`storage-q-ask-${bid.id}`}
      className={`gap-1.5 border-orange-200 text-orange-800 hover:bg-orange-50 ${compact ? "h-7 px-2 text-xs" : "w-full mb-2"}`}>
      <MessageCircleQuestion className="h-4 w-4" /> How would you store my goods?
    </Button>
  );
}

// Removalist side: prompt on their own bid when the customer asked.
export function StorageQuestionAlert({ bid }) {
  const q = bid?.storage_question;
  if (!q?.asked_at || q.answered_at || hasStorage(bid)) return null;
  return (
    <p className="flex items-start gap-1.5 text-xs rounded-md bg-orange-50 border border-orange-200 text-orange-800 px-2.5 py-1.5 mt-1" data-testid={`storage-q-alert-${bid.id}`}>
      <MessageCircleQuestion className="h-3.5 w-3.5 shrink-0 mt-px" /> The customer asked how you'd store their goods. Update your bid with storage.
    </p>
  );
}
